import React from "react";
import { Card } from "@/components/ui/card";
import { Target } from "lucide-react";

function EyeResults({ side, data, target }) {
  if (!data) return null;
  return (
    <div className="rounded-lg border border-slate-200 bg-gradient-to-br from-white to-sky-50/40 p-5">
      <div className="flex items-center justify-between mb-3">
        <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">
          {side} · IOL Power
        </div>
        <div className="text-[10px] text-slate-500">A-const {data.a_constant}</div>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div className="rounded-lg border border-sky-200 bg-white p-4">
          <div className="text-[11px] uppercase tracking-wider text-slate-500">Emmetropia (0.00 D)</div>
          <div className="font-serif text-4xl font-bold text-sky-800 mt-1">
            {formatPower(data.iol_power_emmetropia)}
          </div>
          <div className="text-xs text-slate-500 mt-1">
            Barrett UII · exact <span className="font-mono" data-testid={`${side.slice(0, 2).toLowerCase()}-emme-exact`}>{formatPower(data.iol_power_emmetropia_exact)}</span>
          </div>
        </div>
        <div className="rounded-lg border border-slate-200 bg-white p-4">
          <div className="text-[11px] uppercase tracking-wider text-slate-500">
            Target {target > 0 ? "+" : ""}{target?.toFixed?.(2)} D
          </div>
          <div className="font-serif text-4xl font-bold text-slate-900 mt-1">
            {formatPower(data.iol_power_at_target)}
          </div>
          <div className="text-xs text-slate-500 mt-1">
            Nearest 0.5 D step · exact <span className="font-mono">{formatPower(data.iol_power_at_target_exact)}</span>
          </div>
        </div>
      </div>

      {data.elp && (
        <div
          className="mt-3 rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-600 flex flex-wrap items-center gap-x-3 gap-y-1"
          data-testid={`${side.slice(0, 2).toLowerCase()}-elp-row`}
        >
          <span className="uppercase tracking-wider text-[10px] text-slate-500 font-semibold">ELP</span>
          <span className="font-mono text-slate-900 font-semibold">{data.elp.used.toFixed(2)} mm</span>
          <span className="text-slate-400">·</span>
          <span>anatomical (ACD + {data.elp.c_constant} × LT) <span className="font-mono">{data.elp.anatomical.toFixed(2)}</span></span>
          <span className="text-slate-400">/</span>
          <span>K-AL <span className="font-mono">{data.elp.k_al.toFixed(2)}</span></span>
        </div>
      )}

      <div className="mt-4">
        <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold mb-2">
          Power matrix (± 3 steps @ 0.50 D · predicted spectacle Rx)
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm font-mono">
            <thead>
              <tr className="text-slate-500 text-xs">
                <th className="text-left py-1">IOL power (D)</th>
                <th className="text-right py-1">Predicted Rx</th>
              </tr>
            </thead>
            <tbody>
              {data.power_matrix.map((r, i) => (
                <tr key={r.iol_power} className={r.is_target ? "bg-sky-100/70 font-semibold" : "border-t border-slate-100"}>
                  <td className="py-1.5">{formatPower(r.iol_power)}</td>
                  <td className="py-1.5 text-right">{r.predicted_refraction > 0 ? "+" : ""}{r.predicted_refraction.toFixed(2)} D</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function formatPower(p) {
  if (p === null || p === undefined) return "—";
  const sign = p >= 0 ? "+" : "";
  return `${sign}${p.toFixed(2)} D`;
}

export default function ResultsCard({ result, request }) {
  return (
    <Card className="p-5 sm:p-6 border-slate-200" data-testid="results-card">
      <div className="flex items-center gap-2 mb-4">
        <Target className="w-5 h-5 text-sky-700" />
        <h2 className="font-serif text-2xl font-semibold tracking-tight">Spherical IOL Power</h2>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <EyeResults side="OD (Right)" data={result.od} target={request.target_refraction} />
        <EyeResults side="OS (Left)" data={result.os} target={request.target_refraction} />
      </div>
    </Card>
  );
}
